import React, { useRef, Suspense, useMemo, useEffect, useLayoutEffect } from "react";
import { Virtuoso } from "react-virtuoso";
import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import { Accordion, Text, Group, Badge, Stack, Box } from '@mantine/core';
import { getComments } from '../api'
import { IconMessages } from '@tabler/icons-react';
import {
  scheduleScrollThreadCommentsToBottom,
  scrollThreadCommentsToBottom,
} from "../utils/threadComments";
import { getEditorChromeTheme } from "../utils/editorChromeTheme";

const COMMENT_CARD_ACTIVE_BORDER = "#fe9a00";

const CommentSidebar = () => {

  const {
    threads,
    currentObjectArtboard,
    viewedComments,
    viewedThread,
    setViewedComments,
    setViewedThread,
    viewportThemeOverride,
  } = useObjectStore(
    useShallow((s) => ({
      threads: s.threads,
      currentObjectArtboard: s.currentObjectArtboard,
      viewedComments: s.viewedComments,
      viewedThread: s.viewedThread,
      setViewedComments: s.setViewedComments,
      setViewedThread: s.setViewedThread,
      viewportThemeOverride: s.viewportThemeOverride,
    }))
  );
  const isDark = viewportThemeOverride === "dark";
  const chrome = getEditorChromeTheme(isDark);
  const commentTheme = isDark
    ? {
        heading: chrome.title,
        badgeBg: chrome.bg,
        badgeText: chrome.title,
        cardBg: chrome.bg,
        cardBorder: chrome.border,
        titleActive: chrome.title,
        titleIdle: chrome.muted,
        iconActive: chrome.iconHover,
        iconIdle: chrome.muted,
        panelBorder: chrome.border,
        panelBg: chrome.bg,
        panelCommentDivider: chrome.border,
        userName: chrome.title,
        date: chrome.muted,
        body: chrome.body,
        primary: chrome.iconHover,
      }
    : {
        heading: chrome.title,
        badgeBg: "#F5F5F4",
        badgeText: chrome.body,
        cardBg: "#FFFFFF",
        cardBorder: chrome.border,
        titleActive: chrome.title,
        titleIdle: chrome.muted,
        iconActive: "#FE9A00",
        iconIdle: chrome.muted,
        panelBorder: "#FEF3C6",
        panelBg: "linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)",
        panelCommentDivider: chrome.border,
        userName: chrome.body,
        date: chrome.muted,
        body: chrome.title,
        primary: chrome.title,
      };
  const threadCommentsScrollRef = useRef(null);
  const lastOpenThreadRef = useRef(null);
  const lastCommentCountRef = useRef(0);
  const virtuosoRef = useRef(null);

  const handleOpenAccordion = (id) => {
    if (id == null) return;
    setViewedThread(id);
    loadComments(id);
  };

  const loadComments = (id) => {
    getComments(id)
      .then(res => setViewedComments(res))
  }

  const threadList = useMemo(() => {
    if (!threads?.[currentObjectArtboard]) return [];
    return threads[currentObjectArtboard];
  }, [threads, currentObjectArtboard]);

  const threadCount = threadList.length;

  useEffect(() => {
    const st = useObjectStore.getState();
    const list = st.threads?.[st.currentObjectArtboard] || [];
    const vt = st.viewedThread;
    if (vt == null || !list.length) return;
    const idx = list.findIndex((t) => t.id === vt);
    if (idx >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: idx, align: "center", behavior: "smooth" });
    }
  }, [viewedThread, currentObjectArtboard]);

  useLayoutEffect(() => {
    if (viewedThread == null) {
      lastOpenThreadRef.current = null;
      return;
    }
    if (viewedComments.length === 0) return;

    const len = viewedComments.length;
    const justOpenedThisThread = lastOpenThreadRef.current !== viewedThread;
    lastOpenThreadRef.current = viewedThread;

    const el = threadCommentsScrollRef.current;
    const schedule = () =>
      scheduleScrollThreadCommentsToBottom(() => threadCommentsScrollRef.current);

    if (justOpenedThisThread) {
      lastCommentCountRef.current = len;
      scrollThreadCommentsToBottom(el);
      schedule();
      return;
    }

    const grew = len > lastCommentCountRef.current;
    lastCommentCountRef.current = len;
    if (grew) {
      scrollThreadCommentsToBottom(el);
      schedule();
    }
  }, [viewedThread, viewedComments]);

  return (
    <div
      id="table"
      style={{
        height: "100%",
        paddingBottom: 8,
        paddingLeft: 0,
        paddingRight: 0,
        marginTop: 0,
        marginBottom: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        scrollbarWidth: "none",
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" mb={20}>
        <Text
          fw={600}
          size={11}
          mb={0}
          tt="uppercase"
          style={{
            fontFamily: "Inter, sans-serif",
            color: commentTheme.heading,
            letterSpacing: "0.55px",
            lineHeight: "16.5px",
          }}
        >
          Scene comments
        </Text>
        <Badge
          size="sm"
          mb={0}
          variant="light"
          color="gray"
          styles={{
            root: {
              backgroundColor: commentTheme.badgeBg,
              color: commentTheme.badgeText,
              fontWeight: 500,
              fontSize: 10,
              lineHeight: "15px",
              height: 19,
              minHeight: 19,
              paddingLeft: 8,
              paddingRight: 8,
              borderRadius: 4,
              border: "none",
            },
          }}
        >
          {threadCount}
        </Badge>
      </Group>

      <Accordion
        value={viewedThread}
        variant="separated"
        radius={4}
        onChange={(e) => handleOpenAccordion(e)}
        pb={10}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        styles={{
          panel: { padding: 0 },
          root: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
        }}
      >
        <Virtuoso
          ref={virtuosoRef}
          style={{ flex: 1, minHeight: 0 }}
          data={threadList}
          defaultItemHeight={88}
          increaseViewportBy={{ top: 160, bottom: 320 }}
          computeItemKey={(_, mapped) => mapped.id}
          itemContent={(_index, mapped) => (
            <Suspense key={mapped.id} fallback={null}>
              <Accordion.Item
                value={mapped.id}
                mb={8}
                style={{
                  borderWidth: 2,
                  borderColor:
                    viewedThread === mapped.id ? COMMENT_CARD_ACTIVE_BORDER : commentTheme.cardBorder,
                  backgroundColor: commentTheme.cardBg,
                  borderRadius: 4,
                  boxShadow:
                    viewedThread === mapped.id
                      ? "0px 0px 0px 0px #fe9a00, 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.1)"
                      : undefined,
                }}
              >
                <Accordion.Control
                  styles={{
                    chevron: {
                      color: commentTheme.primary,
                    },
                  }}
                >
                  <Group gap={8} wrap="nowrap">
                    <IconMessages
                      size={22}
                      color={viewedThread === mapped.id ? commentTheme.iconActive : commentTheme.iconIdle}
                    />
                    <Text
                      style={{
                        textOverflow: "ellipsis",
                        maxWidth: "180px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        lineHeight: "20px",
                        letterSpacing: "-0.15px",
                        color: viewedThread === mapped.id ? commentTheme.titleActive : commentTheme.titleIdle,
                      }}
                      fw={viewedThread === mapped.id ? 500 : 400}
                      size="sm"
                    >
                      {new Date(mapped.last_updated).toLocaleDateString()}: {mapped.latest_comment.comment_content}
                    </Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {viewedThread === mapped.id && (
                    <Box
                      px={12}
                      pt={14}
                      pb={14}
                      style={{
                        borderTop: `1px solid ${commentTheme.panelBorder}`,
                        background: commentTheme.panelBg,
                      }}
                    >
                      <Box
                        ref={threadCommentsScrollRef}
                        style={{ maxHeight: 220, overflowY: "auto", overflowX: "hidden" }}
                      >
                        <Stack gap={0}>
                          {viewedComments.map((c, idx) => (
                            <Box
                              key={c.id}
                              pt={idx > 0 ? 14 : 0}
                              mt={idx > 0 ? 14 : 0}
                              style={idx > 0 ? { borderTop: `1px solid ${commentTheme.panelCommentDivider}` } : undefined}
                            >
                              <UserComment
                                id={c.id}
                                threadId={mapped.id}
                                userNameProp={c.author}
                                commentDateProp={new Date(c.created_at).toLocaleDateString()}
                                fullTextProp={c.comment_content}
                                theme={commentTheme}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Suspense>
          )}
        />
      </Accordion>
    </div>
  )
}


export default CommentSidebar


const UserComment = ({ userNameProp, commentDateProp, fullTextProp, theme }) => {
  return (
    <Stack gap={8}>
      <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
        <Text
          size="xs"
          fw={600}
          title={userNameProp}
          style={{
            fontFamily: "Inter, sans-serif",
            color: theme.userName,
            lineHeight: "16px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {String(userNameProp ?? "").toUpperCase()}
        </Text>
        <Text
          size="xs"
          fw={400}
          style={{
            color: theme.date,
            lineHeight: "16px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {commentDateProp}
        </Text>
      </Group>
      <Text
        size="sm"
        fw={400}
        style={{
          color: theme.body,
          lineHeight: "22px",
          letterSpacing: "-0.15px",
        }}
      >
        {fullTextProp}
      </Text>
    </Stack>
  );
};
